'use client'

import { TrackingInsiderObject } from '@/components/analytics/tracking-insider-object'
import { ProductCard } from '@/components/common'
import { ProductCardSkeleton } from '@/components/common/product-card/product-item-skeleton'
import { Button } from '@/components/ui/button'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { TGenderType } from '@/types/product'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useCollection } from '../_hooks/use-collection'
import { CollectionEmptyProductList } from './empty-product-list'

type Props = {
  slug: string
  className?: string
  isFlatten?: boolean
  genderType?: TGenderType
  filter?: {
    [key: string]: string | number | boolean
  }[]
}

export const CollectionListing = ({
  slug,
  className,
  isFlatten,
  genderType,
  filter,
}: Props) => {
  const {
    allProducts,
    totalProducts,
    currentShowing,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    handleLoadMore,
  } = useCollection({ slug, isFlatten, genderType, filterDefault: filter })

  const t = useTranslations()

  if (isLoading && allProducts.length === 0) {
    return (
      <div className='w-full flex-1' id='collection-listing'>
        <div
          className={cn(
            'grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4',
            className
          )}
        >
          {Array.from({ length: 12 }).map((_, index) => (
            <ProductCardSkeleton key={index} />
          ))}
        </div>
      </div>
    )
  }

  const insiderObjectListing = {
    items: allProducts.map((product) => ({
      id: product.id,
      name: product.title,
      currency: 'VND',
      taxonomy: [],
      unit_price: product.compare_price,
      unit_sale_price: product.regular_price,
      url: `https://www.coolmate.me${product.href}`,
      product_image_url: ImageHelper.getImageUrl(product.default_thumbnail)
        .replace(/\(/g, '%28')
        .replace(/\)/g, '%29'),
    })),
  }

  return (
    <>
      <div className='w-full flex-1' id='collection-listing'>
        <div
          className={cn(
            'grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4',
            className
          )}
        >
          {allProducts.map((product, index) => (
            <ProductCard product={product} key={`${product.id}-${index}`} />
          ))}
          {allProducts.length === 0 && !isFetchingNextPage && (
            <CollectionEmptyProductList />
          )}
          {isFetchingNextPage &&
            Array.from({ length: 4 }).map((_, index) => (
              <ProductCardSkeleton key={`loading-${index}`} />
            ))}
        </div>
        {hasNextPage && (
          <div className='mt-10 text-center lg:mt-17'>
            <Button
              variant='black'
              className='group'
              onClick={handleLoadMore}
              disabled={isFetchingNextPage}
            >
              {t('button.view_more')}{' '}
              <Image
                src='/icons/icon-arrow-line-right.svg'
                width={16}
                height={19}
                alt='icon arrow right'
                className='mb-1 h-5 w-4 transition-transform duration-200 group-hover:translate-x-1'
              />
            </Button>
            <p className='mt-1 font-sans text-sm leading-4.5 text-neutral-900/60 lg:mt-1.5 lg:text-base lg:leading-5'>
              {t('collection_page.message.display_product_count', {
                currentShowing,
                totalProducts,
              })}
            </p>
          </div>
        )}
      </div>
      <TrackingInsiderObject keyName='listing' value={insiderObjectListing} />
    </>
  )
}
